// Single source of truth for merging a per-client brand profile and the
// system-wide brand defaults into a BrandResolverContext. Templates and other
// preview surfaces resolve `$brand.X` merge tags against this shape, so any
// drift between callers (e.g. forgetting to populate `borderRadius` on one
// surface) silently breaks rendering — every consumer must go through here.

import type { BrandResolverContext } from '@nexmail/shared-util/email-builder';
import type { GetBrandProfileResponseDto } from '@/apis/backend/generated/models/getBrandProfileResponseDto';
import type { SystemBrandDefaultsResponseDto } from '@/apis/backend/generated/models/systemBrandDefaultsResponseDto';

// Required maps + slogan/collections — strict shape so the value also
// satisfies DynamicPickerBrand consumers without extra coercion.
export interface ResolvedBrand extends BrandResolverContext {
  colorByUsage: Record<string, string>;
  fontByUsage: Record<string, string>;
  socialByPlatform: Record<string, string>;
}

// Shape the preview context already has on hand from /v1/products — projected
// into the BrandResolverContext.products array so chat-template previews can
// resolve `$product[N].(name|price|imageUrl|url)` without a second fetch.
export interface BrandResolverProductInput {
  productTitle?: string | null;
  variantPrice?: number | null;
  variantImageUrl?: string | null;
  productHandle?: string | null;
}

export function buildBrandResolverContext(
  brandProfile: GetBrandProfileResponseDto | undefined,
  systemDefaults: SystemBrandDefaultsResponseDto | undefined,
  products?: BrandResolverProductInput[],
): ResolvedBrand | undefined {
  if (!brandProfile && !systemDefaults) return undefined;

  const data = (brandProfile?.data ?? {}) as Record<string, unknown>;

  const colorByUsage: Record<string, string> = { ...(systemDefaults?.colorByUsage ?? {}) };
  for (const c of brandProfile?.colors ?? []) {
    if (c.usage && c.hexValue) colorByUsage[c.usage] = c.hexValue;
  }
  // `border` is a valid brand-color usage but almost no brand sets one and
  // system defaults don't seed it, so templates referencing
  // `$brand.color.border` would otherwise leak the literal token. Derive a
  // soft tint from primary (or text/accent) so the border still picks up the
  // brand subtly. Hard fallback for color-less brands.
  if (!colorByUsage.border) {
    const base =
      colorByUsage.primary ?? colorByUsage.text ?? colorByUsage.accent ?? null;
    colorByUsage.border = base ? softenHex(base, 0.85) : '#E5E7EB';
  }
  // `$brand.color.button` must always resolve to a hex — leaving the raw
  // token in the document means inserted sections render as unstyled text on
  // brands whose `system_brand_defaults` / `brand_colors` don't carry an
  // explicit button value. Mirrors the api template-filler fallback so
  // client-side section inserts and server-side template fills agree.
  if (!colorByUsage.button) {
    colorByUsage.button = colorByUsage.primary ?? '#111827';
  }
  // Map DB snake_case `button_secondary` → camelCase `buttonSecondary` so
  // `$brand.color.buttonSecondary` resolves. Chain: explicit snake_case row
  // → accent → border → button. Also mirrors api template-filler.
  if (!colorByUsage.buttonSecondary) {
    colorByUsage.buttonSecondary =
      colorByUsage.button_secondary ??
      colorByUsage.accent ??
      colorByUsage.border ??
      colorByUsage.button;
  }
  // Section-background usages MUST resolve to a hex too — without these, a
  // section authored with `background-color:$brand.color.background` (or
  // `surface`) leaks the literal token, which the browser reads as invalid CSS
  // and paints WHITE. That made library sections render white in the editor
  // while the server-compiled email showed the real color. Mirrors the api
  // template-filler loadBrand fallbacks so editor and send-time agree.
  if (!colorByUsage.background) colorByUsage.background = '#FFFFFF';
  if (!colorByUsage.emailBackground) colorByUsage.emailBackground = '#FFFFFF';
  if (!colorByUsage.surface) colorByUsage.surface = darkenHex(colorByUsage.background, 0.04);
  if (!colorByUsage.text) colorByUsage.text = '#111827';
  if (!colorByUsage.link) colorByUsage.link = colorByUsage.primary ?? '#2563EB';
  if (!colorByUsage.accent) {
    colorByUsage.accent = colorByUsage.primary ?? colorByUsage.secondary ?? '#111827';
  }

  const fontByUsage: Record<string, string> = { ...(systemDefaults?.fontByUsage ?? {}) };
  for (const f of brandProfile?.fonts ?? []) {
    if (f.usage && f.fontFamily) fontByUsage[f.usage] = f.fontFamily;
  }
  // Convenience alias: `heading` == `heading_1` when no explicit heading entry.
  if (!fontByUsage.heading && fontByUsage.heading_1) {
    fontByUsage.heading = fontByUsage.heading_1;
  }

  const socialByPlatform: Record<string, string> = { ...(systemDefaults?.socialByPlatform ?? {}) };
  const rawSocial = (data.socialLinks ?? {}) as Record<string, unknown>;
  for (const [platform, href] of Object.entries(rawSocial)) {
    if (typeof href === 'string') socialByPlatform[platform.toLowerCase()] = href;
  }

  const website =
    (data.website as string | undefined) ?? systemDefaults?.website ?? undefined;
  // Same shape resolveProductPath() expects: name/productUrl/imageUrl/price.
  // productUrl falls back to the brand website when no handle is available —
  // server-side resolution builds a proper /products/{handle} URL via the
  // product service. Preview just needs a clickable destination.
  const resolvedProducts = (products ?? [])
    .filter((p) => p.productTitle)
    .map((p) => ({
      name: p.productTitle ?? undefined,
      productUrl:
        p.productHandle && website
          ? `${website.replace(/\/$/, '')}/products/${p.productHandle}`
          : website,
      imageUrl: p.variantImageUrl ?? undefined,
      price: p.variantPrice != null ? `$${Number(p.variantPrice).toFixed(2)}` : undefined,
    }));

  return {
    name: (data.brandName as string | undefined) ?? systemDefaults?.brandName ?? undefined,
    description: (data.description as string | undefined) ?? systemDefaults?.description ?? undefined,
    tagline: (data.tagline as string | undefined) ?? systemDefaults?.tagline ?? undefined,
    slogan: (data.slogan as string | undefined) ?? undefined,
    website,
    email:
      (data.contactEmail as string | undefined) ??
      (data.defaultFromEmail as string | undefined) ??
      systemDefaults?.email ??
      undefined,
    address: formatAddress(data.companyAddress) ?? systemDefaults?.address ?? undefined,
    industry: (data.industry as string | undefined) ?? systemDefaults?.industry ?? undefined,
    // Prefer the server-computed proxied URL so the preview iframe doesn't
    // hotlink the brand's raw CDN (NitroCDN, Shopify, etc. block requests with
    // a non-origin Referer, leaving a broken <img>). Falls back to the raw URL
    // and finally to system defaults — same chain as before.
    logoUrl:
      (data.logoUrlEmailOptimized as string | undefined) ??
      (data.logoUrl as string | undefined) ??
      systemDefaults?.logoUrl ??
      undefined,
    borderRadius: normalizeBorderRadius(
      (data.borderRadius as string | number | undefined) ?? undefined,
    ),
    colorByUsage,
    fontByUsage,
    socialByPlatform,
    navLinks: brandProfile?.navLinks
      ?.filter((l) => l.title && l.url)
      .map((l) => ({ label: l.title, href: l.url })),
    // Per-client collections win; fall through to system defaults so picker
    // previews still resolve `$brand.collections[N]` to readable text when
    // the active brand isn't connected to a commerce platform.
    collections:
      brandProfile?.collections && brandProfile.collections.length > 0
        ? brandProfile.collections.map((c) => ({
            title: c.title,
            url: c.url,
            imageUrl: c.imageUrl ?? undefined,
          }))
        : systemDefaults?.collections && systemDefaults.collections.length > 0
          ? systemDefaults.collections.map((c) => ({
              title: c.title,
              url: c.url,
              imageUrl: c.imageUrl ?? undefined,
            }))
          : undefined,
    products: resolvedProducts.length > 0 ? resolvedProducts : undefined,
  };
}

// Coerce a brand-profile border radius (raw number, number-like string, or
// already CSS-ready like "6px") into a CSS-ready string.
function normalizeBorderRadius(raw: string | number | undefined): string | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw)) return `${raw}px`;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
    return trimmed;
  }
  return undefined;
}

// Mix a hex color toward white by `t` (0 = unchanged, 1 = pure white). Used
// to derive a soft border tint from a brand's primary color when the brand
// doesn't define an explicit border swatch. Returns the input unchanged for
// non-hex strings (CSS keywords, rgb()) so callers don't crash on legacy data.
function softenHex(hex: string, t: number): string {
  const parsed = parseHex(hex);
  if (!parsed) return hex;
  const mix = (channel: number) => Math.round(channel * (1 - t) + 255 * t);
  const r = mix(parsed.r);
  const g = mix(parsed.g);
  const b = mix(parsed.b);
  return '#' + [r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('');
}

// Mix a hex color toward black by `t` (0 = unchanged). Mirrors the api
// template-filler's darkenHex so the `surface` fallback matches the server.
function darkenHex(hex: string, t: number): string {
  const parsed = parseHex(hex);
  if (!parsed) return hex;
  const mix = (channel: number) => Math.round(channel * (1 - t));
  const r = mix(parsed.r);
  const g = mix(parsed.g);
  const b = mix(parsed.b);
  return '#' + [r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('');
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  if (typeof hex !== 'string' || !hex.startsWith('#')) return null;
  let v = hex.slice(1).toLowerCase();
  if (v.length === 3) v = v.split('').map((c) => c + c).join('');
  if (v.length !== 6 || /[^0-9a-f]/.test(v)) return null;
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  };
}

function formatAddress(raw: unknown): string | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const a = raw as Record<string, unknown>;
  const parts = [a.street, a.street2, a.city, a.state, a.zip ?? a.postalCode, a.country]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.trim());
  return parts.length > 0 ? parts.join(', ') : undefined;
}
